import React from "react";
import LoadingIndicator from "../shared/LoadingIndicator";
import {
  Card,
  ProgressBar,
  Checkbox,
  Icon
} from "@blueprintjs/core";
import Flexbox from "flexbox-react";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const EmployeeOnboardingDetails = ({
  employee,
  steps = [],
  toggleTaskStatus,
  isLoading
}) => {
  // Calculate progress based on tasks
  const calculateProgress = () => {
    let totalTasks = 0;
    let completedTasks = 0;
    steps.forEach((step) => {
      (step.tasks || []).forEach((task) => {
        totalTasks++;
        if (task.status) completedTasks++;
      });
    });
    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    return {
      progress,
      completedTasks,
      totalTasks
    };
  };

  const { progress, completedTasks, totalTasks } =
    calculateProgress();

  if (isLoading) {
    return (
      <div className="loading-container">
        <LoadingIndicator size={30} />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="no-data">
        Select an employee to view onboarding details.
      </div>
    );
  }

  return (
    <div className="employee-onboarding-details">
      {/* Header */}
      <Card className="header-card">
        <div className="header-top">
          <div className="employee-info">
            <div className="employee-avatar">
              {getInitials(employee.name)}
            </div>
            <div>
              <div className="employee-header-name">
                {employee.name}
              </div>
              <div className="employee-meta">
                {employee.role || employee.jobProfile || "Employee"}
                {employee.email && ` • ${employee.email}`}
              </div>
            </div>
          </div>

          <div className="progress-text">
            {progress}%
            <div className="steps-count">
              {completedTasks}/{totalTasks} tasks
            </div>
          </div>
        </div>

        <ProgressBar value={progress / 100} intent="primary" stripes={false} />
      </Card>

      {/* Steps & Tasks */}

      <div className="steps-container">
        {steps.length === 0 ? (
          <div className="no-data">
            No onboarding steps available.
          </div>
        ) : (
          steps.map((step) => (
            <Flexbox display="flex" flexDirection="column" gap="10px" key={step.id} className="push-10-b">
              {/* Step Title */}
              <div className="step-header">
                <div className="step-title">{step.name}</div>
              </div>

              {/* Tasks */}
              <div className="task-list">
                {!step.tasks || step.tasks.length === 0 ? (
                  <div className="no-task">
                    No tasks in this step
                  </div>
                ) : (
                  step.tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`task-card ${task.status ? "completed" : ""}`}
                    >
                      {/* Left Section */}
                      <div className="task-left">
                        <Checkbox
                          checked={task.status}
                          onChange={() =>
                            toggleTaskStatus(step.id, task.id)
                          }
                          className="task-checkbox"
                        />

                        <div className="task-details">
                          <div
                            className={`task-title ${task.status ? "completed" : ""}`}
                          >
                            {task.name}
                          </div>

                          {task.due_date && (
                            <div className="task-meta">
                              <Icon icon="calendar" size={12} />
                              <span>
                                Due {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Section */}
                      <div className="task-right">
                        {task.due_date && (
                          <span className="due-badge">
                            {new Date(task.due_date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        )}

                        {task.status && (
                          <Icon
                            icon="tick-circle"
                            intent="success"
                            className="task-success-icon"
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Flexbox>
          ))
        )}
      </div>
    </div>
  );
};

export default EmployeeOnboardingDetails;
